import React, { FC, useEffect, useState } from "react"
import { KButton, KSpan, KTitleSpan } from "kahuna-base-react-components"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from "chart.js"
import { Line } from "react-chartjs-2"
import { handleAuthorizedRequest, users } from "../../../../../apis"
import { mapToCamelCase, addCommasToNumber, replaceCurrencies } from "../../../../../utility"
import { AdvanceDealDetail, AdvanceDealRow, DealDetailMonth, UserAdvanceProgressValues } from "../../../../../types"
import { STATUS_STYLE } from "./dealStatus"
import AdvanceDealDetailsSections from "./AdvanceDealDetailsSections"
import ContractTermsCard from "./ContractTermsCard"
import TargetActualPlot from "./TargetActualPlot"
import Loading from "../../../../ui/Loading"
import PlatformCompletenessInfo, { recentMonths } from "../../../../ui/components/PlatformCompletenessInfo"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

const GREEN = "#22c55e"
const RED = "#ef4444"
const TEXT_MUTED = "#6b7280"
const TEXT_PRIMARY = "#111"
const CHART_FONT = "Inter var, Inter, system-ui, -apple-system, sans-serif"

type AdvanceDealDetailPageProps = {
  selectedDeal: AdvanceDealRow
  // The deal was fetched with the portfolio's period filter, so its month keys follow that choice.
  reportingPeriod: boolean
  onBack: () => void
}

// ---- formatting -------------------------------------------------------------

const usd0 = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `$${addCommasToNumber(Math.round(value))}`

const compactUsd = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`
  return `$${Math.round(value)}`
}

const pct = (value: number | null | undefined, digits = 0) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${parseFloat(value.toFixed(digits))}%`

const multipleX = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${value.toFixed(2)}x`

const xirrPct = (fraction: number | null | undefined) =>
  fraction === null || fraction === undefined || isNaN(fraction) ? "N/A" : `${(fraction * 100).toFixed(1)}%`

const monthYear = (key: string | null | undefined) => {
  if (!key) return "N/A"
  const year = key.slice(0, 4)
  const month = parseInt(key.slice(4, 6), 10)
  if (!year || isNaN(month)) return "N/A"
  return new Date(parseInt(year, 10), month - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" })
}

const monthYearShort = (key: string) => {
  const year = key.slice(2, 4)
  const month = parseInt(key.slice(4, 6), 10)
  if (isNaN(month)) return key
  return `${new Date(2000, month - 1, 1).toLocaleDateString("en-US", { month: "short" })} ${year}`
}

// A freshly funded advance has no reported royalties and, until the first projection run,
// no projections either. Object.keys(...).reduce() with no seed throws on an empty array,
// which is what took the whole detail page down for newly funded artists.
const latestMonth = (months: { [key: string]: number } | null | undefined): string | null => {
  const keys = Object.keys(months || {})
  return keys.length === 0 ? null : keys.reduce((a, b) => (a > b ? a : b))
}

const monthKeys = (months: { [key: string]: number } | null | undefined): string[] => Object.keys(months || {})

const monthsText = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${Math.round(value)} months`

const monthsShort = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${Math.round(value)} mo`

const timingText = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value)
    ? "N/A"
    : `${value > 0 ? "+" : ""}${Math.round(value)} mo`

const addMonthsToKey = (key: string, months: number) => {
  const year = parseInt(key.slice(0, 4), 10)
  const month = parseInt(key.slice(4, 6), 10)
  if (isNaN(year) || isNaN(month)) return key
  const shifted = new Date(year, month - 1 + months, 1)
  return `${shifted.getFullYear()}${String(shifted.getMonth() + 1).padStart(2, "0")}`
}

const monthsBetweenKeys = (from: string, to: string) => {
  const fromYear = parseInt(from.slice(0, 4), 10)
  const fromMonth = parseInt(from.slice(4, 6), 10)
  const toYear = parseInt(to.slice(0, 4), 10)
  const toMonth = parseInt(to.slice(4, 6), 10)
  if ([fromYear, fromMonth, toYear, toMonth].some(isNaN)) return 0
  return (toYear - fromYear) * 12 + (toMonth - fromMonth)
}

// Every progress map on the row is optional in practice; normalise so callers can index freely.
const progressValues = (values: UserAdvanceProgressValues | null | undefined): UserAdvanceProgressValues => ({
  royalties: values?.royalties || {},
  projections: values?.projections || {},
  advances: values?.advances || {}
})

// ---- small components -------------------------------------------------------

const Badge: FC<{ text: string; bg: string; color: string }> = ({ text, bg, color }) => (
  <span
    style={{
      backgroundColor: bg,
      color,
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.04em",
      whiteSpace: "nowrap"
    }}
  >
    {text}
  </span>
)

const KpiCard: FC<{
  label: string
  value: string
  subtext?: string
  subRows?: { label: string; value: string }[]
  subtextColor?: string
}> = ({ label, value, subtext, subRows, subtextColor = TEXT_MUTED }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-1.5">
    <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
    <KTitleSpan text={value} fontSize={22} fontWeight={700} color={TEXT_PRIMARY} />
    {subtext ? <KSpan text={subtext} fontSize={12} color={subtextColor} /> : null}
    {subRows?.map((row) => (
      <div key={row.label} className="flex justify-between">
        <KSpan text={row.label} fontSize={12} color={subtextColor} lineHeight="18px" />
        <KSpan text={row.value} fontSize={12} fontWeight={700} color={subtextColor} lineHeight="18px" />
      </div>
    ))}
  </div>
)

const RecoveryStatusCard: FC<{ selectedDeal: AdvanceDealRow }> = ({ selectedDeal }) => {
  const current = STATUS_STYLE[selectedDeal.status] || { label: selectedDeal.status, bg: "#f1f5f9", color: TEXT_MUTED }
  const previous = selectedDeal.previousStatus
  const delta = selectedDeal.recoupTimingDelta
  const hasDelta = delta !== null && delta !== undefined && !isNaN(delta) && Math.round(delta) !== 0
  const deltaColor = !hasDelta ? TEXT_PRIMARY : delta! < 0 ? "#b91c1c" : "#15803d"
  const metrics = [
    { label: "Target", value: monthsShort(selectedDeal.targetRecoupMonths), color: TEXT_PRIMARY },
    { label: "Projected", value: monthsShort(selectedDeal.companyRecoupLag), color: TEXT_PRIMARY },
    { label: "Delta", value: timingText(delta), color: deltaColor }
  ]

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-1.5">
      <KSpan text="RECOVERY STATUS" fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
      {/* The hidden title gives this row exactly the height of the big value on the other cards,
          so label, middle section and bottom lines all land at the same level across the grid. */}
      <div className="relative">
        <div style={{ visibility: "hidden" }} aria-hidden>
          <KTitleSpan text="0" fontSize={22} fontWeight={700} color={TEXT_PRIMARY} />
        </div>
        <div className="absolute inset-0 flex items-center gap-2 flex-wrap">
          {/* Same component as the big values on the other cards, so the font family, weight and
              tracking match; the pill only adds the background around it. */}
          <KTitleSpan
            text={previous ? STATUS_STYLE[previous]?.label ?? previous : "N/A"}
            fontSize={22}
            fontWeight={700}
            lineHeight="30px"
            color={previous ? TEXT_PRIMARY : TEXT_MUTED}
          />
          <KTitleSpan text="→" fontSize={22} fontWeight={700} lineHeight="30px" color={TEXT_MUTED} />
          <span
            style={{
              backgroundColor: current.bg,
              padding: "2px 12px",
              borderRadius: 999,
              whiteSpace: "nowrap"
            }}
          >
            <KTitleSpan text={current.label} fontSize={22} fontWeight={700} lineHeight="30px" color={current.color} />
          </span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {metrics.map((metric) => (
          <div key={metric.label} className="min-w-0">
            <KSpan text={metric.label} fontSize={12} color={TEXT_MUTED} lineHeight="18px" />
            <div>
              <KSpan text={metric.value} fontSize={12} fontWeight={700} color={metric.color} lineHeight="18px" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const ProgressBar: FC<{ label: string; pctValue: number; color: string; recouped: number; total: number }> = ({
  label,
  pctValue,
  color,
  recouped,
  total
}) => (
  <div className="flex flex-col gap-2">
    <div className="flex justify-between items-baseline">
      <KSpan text={label} fontSize={12} fontWeight={500} color={TEXT_MUTED} letterSpacing="0.03em" lineHeight="16px" />
      <div className="flex items-baseline gap-2">
        {/* Amount recouped over the amount deployed, quietly beside the percentage. */}
        <KSpan
          text={`${usd0(Math.max(recouped, 0))} / ${usd0(total)}`}
          fontSize={11}
          color="#9ca3af"
          lineHeight="18px"
        />
        <KSpan text={`${Math.round(pctValue)}%`} fontSize={15} fontWeight={500} color={TEXT_PRIMARY} lineHeight="18px" />
      </div>
    </div>
    <div style={{ height: 8, background: "#f1f5f9", borderRadius: 999, overflow: "hidden" }}>
      <div style={{ width: `${Math.min(100, pctValue)}%`, height: "100%", background: color, borderRadius: 999 }} />
    </div>
  </div>
)

// One column of the recoup timeline; every column after the first is split off by a hairline.
const Milestone: FC<{ label: string; value: string; first?: boolean }> = ({ label, value, first }) => (
  <div className={`flex flex-col gap-1 min-w-0 ${first ? "" : "border-l border-gray-100 pl-3"}`}>
    <KSpan text={label} fontSize={11.5} fontWeight={500} color={TEXT_MUTED} letterSpacing="0.04em" lineHeight="16px" />
    <KSpan text={value} fontSize={13} fontWeight={500} color={TEXT_PRIMARY} lineHeight="18px" />
  </div>
)

// Solid header pill (Company / Artist recoup months).
const RecoupPill: FC<{ text: string; bg: string }> = ({ text, bg }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      backgroundColor: bg,
      padding: "3px 12px",
      borderRadius: 999,
      whiteSpace: "nowrap"
    }}
  >
    <KSpan text={text} fontSize={13} fontWeight={500} color="#fff" lineHeight="20px" />
  </span>
)

const thStyle: React.CSSProperties = {
  textAlign: "right",
  padding: "10px 12px",
  fontSize: 11,
  fontWeight: 600,
  color: TEXT_MUTED,
  letterSpacing: "0.03em",
  borderBottom: "1px solid #e5e7eb"
}

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontSize: 13,
  color: TEXT_PRIMARY,
  borderBottom: "1px solid #f1f5f9",
  whiteSpace: "nowrap",
  textAlign: "right"
}

// ---- expected vs actual recoup ----------------------------------------------

// Portfolio Overview grades every deal against its recoup target, but the detail page only
// ever showed actuals and projections — so you could see a deal was flagged without seeing
// why. This is the target itself: the straight line the deal was underwritten to reach, drawn
// against collections actually reported. Deliberately no projections; the point is whether
// the deal is keeping up with its promise, not what the forecast hopes for.
const TargetVsActualChart: FC<{ selectedDeal: AdvanceDealRow }> = ({ selectedDeal }) => {
  const { targetRecoupMonths } = selectedDeal
  const { dealDate, recoupTarget } = selectedDeal
  const collections = selectedDeal.royalties
  const reportedThrough = latestMonth(selectedDeal.royalties)

  if (!dealDate || !recoupTarget || !targetRecoupMonths || targetRecoupMonths <= 0) return null

  const monthsReported = reportedThrough ? monthsBetweenKeys(dealDate, reportedThrough) : 0
  // Always show the full target window, plus any overrun, so the finish line stays on screen.
  const spanMonths = Math.max(targetRecoupMonths, monthsReported) + 1
  const axis = Array.from({ length: spanMonths + 1 }, (_, index) => addMonthsToKey(dealDate, index))

  let cumulative = 0
  const actual: (number | null)[] = []
  axis.forEach((month) => {
    cumulative += collections?.[month] ?? 0
    actual.push(reportedThrough !== null && month <= reportedThrough ? cumulative : null)
  })

  // Expected monthly collections ramp up: nothing in the deployment month and the month after, then
  // 0.25x, 0.75x and a full x every month from the fourth on. That makes the cumulative curve
  // x * (months - 2), so x is the monthly pace that lands exactly on the target at the target month.
  // A target of two months or less has no room for the ramp and stays a straight line.
  const monthlyPace = targetRecoupMonths > 2 ? recoupTarget / (targetRecoupMonths - 2) : null
  const expectedAt = (index: number) => {
    if (monthlyPace === null) return Math.min(recoupTarget, (recoupTarget * Math.max(index, 0)) / targetRecoupMonths)
    if (index <= 1) return 0
    if (index === 2) return 0.25 * monthlyPace
    return Math.min(recoupTarget, monthlyPace * (index - 2))
  }
  const expected = axis.map((_, index) => expectedAt(index))

  const actualToDate = cumulative
  const expectedToDate = expectedAt(monthsReported)
  const gap = actualToDate - expectedToDate
  // Within one month of expected collections either way counts as on pace
  const tolerance = monthlyPace ?? 0
  const gapState = gap < -tolerance ? "behind" : gap > tolerance ? "ahead" : "within"
  const attainment = expectedToDate > 0 ? (actualToDate / expectedToDate) * 100 : null

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 min-w-0 flex-1 flex flex-col">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex flex-col gap-3">
          <KTitleSpan
            text="Target vs Actual Recoup"
            fontSize={20}
            fontWeight={700}
            lineHeight="28px"
          />
          <KSpan
            text={`Underwritten to collect ${usd0(recoupTarget)} within ${targetRecoupMonths} months of ${monthYear(
              dealDate
            )}`}
            fontSize={13}
            color={TEXT_MUTED}
          />
        </div>
        <Badge
          text={
            gapState === "within"
              ? "WITHIN TARGET RANGE"
              : gapState === "ahead"
              ? `${usd0(gap)} AHEAD OF TARGET`
              : `${usd0(Math.abs(gap))} BELOW EXPECTED TO DATE`
          }
          bg={gapState === "ahead" ? "#dcfce7" : gapState === "behind" ? "#fee2e2" : "#f1f5f9"}
          color={gapState === "ahead" ? "#15803d" : gapState === "behind" ? "#b91c1c" : TEXT_MUTED}
        />
      </div>
      <div className="flex-1 min-h-0 flex flex-col" style={{ marginTop: 16 }}>
        <TargetActualPlot
          labels={axis.map((month) => monthYearShort(month))}
          fullLabels={axis.map((month) => monthYear(month))}
          expected={expected}
          actual={actual}
          tolerance={tolerance}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <SummaryTile label="EXPECTED TO DATE" value={usd0(expectedToDate)} />
        <SummaryTile label="ACTUAL TO DATE" value={usd0(actualToDate)} />
        <SummaryTile label="ATTAINMENT" value={attainment === null ? "N/A" : pct(attainment)} />
      </div>
    </div>
  )
}

const SummaryTile: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between items-center gap-3 p-2.5 border border-gray-100 rounded-xl bg-gray-50">
    <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" lineHeight="16px" />
    <KTitleSpan text={value} fontSize={18} fontWeight={700} color={TEXT_PRIMARY} lineHeight="24px" />
  </div>
)

// ---- monthly breakdown table ------------------------------------------------

const MonthlyBreakdownTable: FC<{ selectedDeal: AdvanceDealRow; reportingPeriod: boolean }> = ({
  selectedDeal,
  reportingPeriod
}) => {
  const [showAll, setShowAll] = useState(false)

  const projectionMonths = monthKeys(selectedDeal.projections)
  const royaltiesMonths = monthKeys(selectedDeal.royalties)

  const months = [...royaltiesMonths, ...projectionMonths]

  const sortedAllMonths = [...months].sort((a, b) => (a < b ? 1 : -1))
  const sortedRoyaltiesMonths = [...royaltiesMonths].sort((a, b) => (a < b ? 1 : -1))

  const visibleMonths = showAll ? sortedAllMonths : sortedRoyaltiesMonths

  // Only reported months can have platforms missing; projection months have no reports at all.
  const completenessMonths = recentMonths(sortedRoyaltiesMonths)

  const renderAggragateRow = (
    userAdvanceProgressValues: UserAdvanceProgressValues | null | undefined,
    month: string,
    color = false
  ) => {
    const { royalties, projections } = progressValues(userAdvanceProgressValues)
    let value = undefined
    let valutStr = "-"
    if (royalties[month] !== undefined) {
      valutStr = `${replaceCurrencies("USD")} ${addCommasToNumber(royalties[month].toFixed(2))}`
      value = royalties[month]
    } else if (projections[month] !== undefined) {
      valutStr = `${replaceCurrencies("USD")} ${addCommasToNumber(projections[month].toFixed(2))}`
      value = projections[month]
    }

    let colorStyle = {}
    if (color && value !== undefined) colorStyle = { color: value >= 0 ? "green" : "red" }

    return <td style={{ ...tdStyle, ...colorStyle }}>{valutStr}</td>
  }

  const convertToCumulativeMap = (userAdvanceProgressValues: UserAdvanceProgressValues) => {
    // 1. Add royalties (positive)
    const cashflowMap: Record<string, number> = {}

    let firstMonth = ""
    Object.entries(userAdvanceProgressValues.royalties ?? {}).forEach(([month, value]) => {
      if (!firstMonth || month < firstMonth) {
        firstMonth = month
      }
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })

    // 2. Add projections (positive)
    Object.entries(userAdvanceProgressValues.projections ?? {}).forEach(([month, value]) => {
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })

    // 3. Add advances (negative)
    Object.entries(userAdvanceProgressValues.advances ?? {}).forEach(([month, value]) => {
      const monthToUse = month < firstMonth ? firstMonth : month
      cashflowMap[monthToUse] = (cashflowMap[monthToUse] || 0) - (value || 0)
    })

    // 4. Convert to sorted array format
    const cashflow: Record<string, number> = {}
    let currentCash = 0

    Object.keys(cashflowMap)
      .sort()
      .forEach((month) => {
        currentCash += cashflowMap[month]
        cashflow[month] = currentCash
      })

    return { royalties: cashflow, advances: {}, projections: {} }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <KTitleSpan text="Monthly Breakdown" fontSize={16} fontWeight={700} />
      <div style={{ overflowX: "auto", marginTop: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: "left" }}>Month</th>
              <th style={thStyle}>Gross Collections</th>
              <th style={thStyle}>Kahuna Revenue</th>
              <th style={thStyle}>Advance Recoup Applied</th>
              <th style={thStyle}>Artist Payable</th>
              <th style={thStyle}>New Advance Funded</th>
              <th style={thStyle}>Outstanding Advance Balance (EoM)</th>
              <th style={thStyle}>Kahuna Net Cash Flow for IRR</th>
              <th style={thStyle}>Kahuna Balance</th>
            </tr>
          </thead>
          <tbody>
            {visibleMonths.map((month) => {
              const advanceTimelineMap = {
                royalties: selectedDeal.advancesTimeline || {},
                projections: {},
                advances: {}
              }
              const kahunaBalanceMap = {
                royalties: selectedDeal.royalties || {},
                projections: selectedDeal.projections || {},
                advances: selectedDeal.advances || {}
              }
              return (
                <tr key={month} style={{ opacity: projectionMonths.includes(month) ? 0.6 : 1 }}>
                  <td style={{ ...tdStyle, textAlign: "left" }}>
                    <div className="flex items-center gap-1">
                      {monthYear(month)}
                      {projectionMonths.includes(month) && <KSpan color={TEXT_MUTED} fontSize={11} text=" · proj" />}
                      {completenessMonths.includes(month) && (
                        <PlatformCompletenessInfo
                          userId={selectedDeal.userId}
                          months={[month]}
                          reportingPeriod={reportingPeriod}
                          label={monthYear(month)}
                        />
                      )}
                    </div>
                  </td>
                  {renderAggragateRow(selectedDeal.actualGrossAndProjection, month)}
                  {renderAggragateRow(selectedDeal.kahunaRevenue, month)}
                  {renderAggragateRow(selectedDeal.kahunaAdvanceCollection, month)}
                  {renderAggragateRow(selectedDeal.artistPayable, month)}
                  {renderAggragateRow(advanceTimelineMap, month)}
                  {renderAggragateRow(selectedDeal.artistBalance, month, true)}
                  {renderAggragateRow(selectedDeal.kahunaTotal, month)}
                  {renderAggragateRow(convertToCumulativeMap(kahunaBalanceMap), month, true)}
                </tr>
              )
            })}
            {visibleMonths.length === 0 && (
              <tr>
                <td style={{ ...tdStyle, textAlign: "center", color: TEXT_MUTED }} colSpan={9}>
                  No monthly data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {sortedAllMonths.length > sortedRoyaltiesMonths.length && (
        <div className="flex justify-center mt-3">
          <KButton
            text={showAll ? "Hide projections" : `Show projections`}
            background="transparent"
            shadowDisabled
            onClick={() => setShowAll(!showAll)}
          />
        </div>
      )}
    </div>
  )
}

// ---- CSV export -------------------------------------------------------------

const exportMonthlyCsv = (selectedDeal?: AdvanceDealRow) => {
  if (!selectedDeal) return

  const projectionMonths = monthKeys(selectedDeal.projections)
  const royaltiesMonths = monthKeys(selectedDeal.royalties)
  const allMonths = [...royaltiesMonths, ...projectionMonths].sort((a, b) => (a < b ? 1 : -1))

  const getVal = (userAdvanceProgressValues: UserAdvanceProgressValues | null | undefined, month: string): string => {
    const { royalties, projections } = progressValues(userAdvanceProgressValues)
    let value: number | undefined
    if (royalties[month] !== undefined) {
      value = royalties[month]
    } else if (projections[month] !== undefined) {
      value = projections[month]
    }
    return value !== undefined ? value.toFixed(2) : ""
  }

  const convertToCumulativeMap = (userAdvanceProgressValues: UserAdvanceProgressValues) => {
    const cashflowMap: Record<string, number> = {}
    let firstMonth = ""
    Object.entries(userAdvanceProgressValues.royalties ?? {}).forEach(([month, value]) => {
      if (!firstMonth || month < firstMonth) firstMonth = month
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })
    Object.entries(userAdvanceProgressValues.projections ?? {}).forEach(([month, value]) => {
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })
    Object.entries(userAdvanceProgressValues.advances ?? {}).forEach(([month, value]) => {
      const monthToUse = month < firstMonth ? firstMonth : month
      cashflowMap[monthToUse] = (cashflowMap[monthToUse] || 0) - (value || 0)
    })
    const cashflow: Record<string, number> = {}
    let currentCash = 0
    Object.keys(cashflowMap)
      .sort()
      .forEach((month) => {
        currentCash += cashflowMap[month]
        cashflow[month] = currentCash
      })
    return { royalties: cashflow, advances: {}, projections: {} }
  }

  const kahunaBalanceMap = {
    royalties: selectedDeal.royalties || {},
    projections: selectedDeal.projections || {},
    advances: selectedDeal.advances || {}
  }
  const cumulativeMap = convertToCumulativeMap(kahunaBalanceMap)

  const header = [
    "Month",
    "Gross Collections",
    "Kahuna Revenue",
    "Advance Recoup Applied",
    "Artist Payable",
    "New Advance Funded",
    "Outstanding Advance Balance (EoM)",
    "Kahuna Net Cash Flow for IRR",
    "Kahuna Balance"
  ]

  const lines = allMonths.map((month) => {
    const advanceTimelineMap = { royalties: selectedDeal.advancesTimeline || {}, projections: {}, advances: {} }
    const isProjection = projectionMonths.includes(month)
    return [
      `${monthYear(month)}${isProjection ? " · proj" : ""}`,
      getVal(selectedDeal.actualGrossAndProjection, month),
      getVal(selectedDeal.kahunaRevenue, month),
      getVal(selectedDeal.kahunaAdvanceCollection, month),
      getVal(selectedDeal.artistPayable, month),
      getVal(advanceTimelineMap, month),
      getVal(selectedDeal.artistBalance, month),
      getVal(selectedDeal.kahunaTotal, month),
      getVal(cumulativeMap, month)
    ].join(",")
  })

  const csv = [header.join(","), ...lines].join("\n")
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = `deal_${selectedDeal.artistName.replace(/\s+/g, "_")}_monthly.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ---- main -------------------------------------------------------------------

const AdvanceDealDetailPage: FC<AdvanceDealDetailPageProps> = ({ selectedDeal, reportingPeriod, onBack }) => {
  const [loading, setLoading] = useState(false)
  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    )
  }

  if (!selectedDeal) {
    return (
      <div className="px-10 pt-5 pb-10 flex flex-col gap-4">
        <KButton width="100px" onClick={() => onBack()} text="← Back" background="transparent" shadowDisabled />
        <KSpan text="No deal data available for this artist." color="#666" />
      </div>
    )
  }

  return (
    // min-w-0 so the wide monthly breakdown scrolls inside its own container rather than
    // stretching the page past the viewport.
    <div className="px-10 pt-5 pb-10 flex flex-col gap-6 min-w-0 max-w-full">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            className="text-xl text-gray-500 hover:text-gray-800"
            onClick={onBack}
            title="Back to Portfolio Overview"
          >
            ←
          </button>
          {selectedDeal.artistImage ? (
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: `center / cover no-repeat url(${selectedDeal.artistImage})`
              }}
            />
          ) : null}
          <KTitleSpan text={selectedDeal.artistName} fontSize={24} fontWeight={700} color={TEXT_PRIMARY} />
          {selectedDeal.recouped ? (
            <Badge text="RECOUPED" bg="#dcfce7" color="#15803d" />
          ) : (
            <Badge text="PERFORMING" bg="#f9fcdc" color="#767676" />
          )}
          {selectedDeal.isNewDeal && !selectedDeal.recouped ? (
            <Badge text="NEW - EXCLUDED FROM RISK" bg="#ede9fe" color="#5b21b6" />
          ) : null}
          <Badge text="ADV" bg="#ede9fe" color="black" />
        </div>
        <div>
          <KButton text="Export" background="black" textColor="white" onClick={() => exportMonthlyCsv(selectedDeal)} />
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        <RecoveryStatusCard selectedDeal={selectedDeal} />
        <KpiCard
          label="TOTAL DEPLOYED"
          value={usd0(selectedDeal.advance)}
          subtext={
            selectedDeal.advanceCount > 0
              ? `Avg advance ${usd0(selectedDeal.advance / selectedDeal.advanceCount)}`
              : undefined
          }
        />
        <KpiCard
          label="OUTSTANDING"
          value={usd0(selectedDeal.outstanding)}
          subtext={`${pct(selectedDeal.outstandingPctOfDeployed)} of deployed`}
        />
        <KpiCard
          label="RECOUP TARGET"
          value={usd0(selectedDeal.recoupTarget)}
          subtext={`Avg multiple ${multipleX(selectedDeal.recoupMultiplier)}`}
        />
        <KpiCard
          label="TOTAL CASH TO KAHUNA"
          value={usd0(selectedDeal.totalRecouped + selectedDeal.totalProfit)}
          subRows={[
            { label: "Recoupment", value: usd0(selectedDeal.totalRecouped) },
            { label: "Profit", value: usd0(selectedDeal.totalProfit) }
          ]}
        />
        <KpiCard
          label="ARTIST DEALS"
          value={`${selectedDeal.advanceCount} ${selectedDeal.advanceCount === 1 ? "advance" : "advances"}`}
          subRows={[
            { label: "First deployment", value: monthYear(selectedDeal.firstAdvanceDate) },
            { label: "Last deployment", value: monthYear(selectedDeal.latestAdvanceDate) }
          ]}
        />
        {/* Same IRR family and wording as Portfolio Overview, so a figure means the same thing on both screens. */}
        <KpiCard
          label="12M IRR"
          value={xirrPct(selectedDeal.irr12M)}
          subtext="Annualized · first 12 months"
        />
        <KpiCard
          label="24M IRR"
          value={xirrPct(selectedDeal.irr24M)}
          subtext="Annualized · first 24 months"
        />
        <KpiCard
          label="36M IRR"
          value={xirrPct(selectedDeal.irr36M)}
          subtext="Annualized · first 36 months"
        />
        <KpiCard
          label="ECONOMIC MOIC"
          value={multipleX(selectedDeal.economicMoic)}
          subtext="Collected to date ÷ deployed"
        />
      </div>

      {/* Recoup progress */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-5 min-w-0">
        <div className="flex justify-between items-center flex-wrap gap-3 -mb-2">
          <KTitleSpan
            text="Recoup Progress"
            fontSize={20}
            fontWeight={700}
            lineHeight="28px"
          />
          <div className="flex gap-2 flex-wrap">
            <RecoupPill
              text={`Company Recoup: ${monthsText(selectedDeal.companyRecoupLag)}`}
              bg={GREEN}
            />
            <RecoupPill
              text={`Artist Recoup: ${monthsText(selectedDeal.artistRecoupLag)}`}
              bg="#000"
            />
          </div>
        </div>
        <ProgressBar
          label="COMPANY RECOUP"
          pctValue={selectedDeal.companyRecoupPct}
          color={GREEN}
          total={selectedDeal.advance}
          recouped={selectedDeal.advance - selectedDeal.outstanding}
        />
        <ProgressBar
          label="ARTIST RECOUP"
          pctValue={selectedDeal.artistRecoupPct}
          color={TEXT_PRIMARY}
          total={selectedDeal.advance}
          recouped={(selectedDeal.advance * selectedDeal.artistRecoupPct) / 100}
        />
        <div className="grid grid-cols-5 gap-2 pt-1">
          <Milestone first label="DEAL START" value={monthYear(selectedDeal.dealDate)} />
          <Milestone
            label="TODAY"
            value={new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" })}
          />
          <Milestone
            label="COMPANY RECOUP"
            value={monthYear(selectedDeal.companyRecoupDate)}
          />
          <Milestone
            label="ARTIST RECOUP"
            value={monthYear(selectedDeal.artistRecoupDate)}
          />
          <Milestone label="CONTRACT END" value={monthYear(selectedDeal.contractEnd)} />
        </div>
      </div>

      {/* Advance schedule and platform revenue, both from the deal details endpoint */}
      <AdvanceDealDetailsSections
        userId={selectedDeal.userId}
        artistName={selectedDeal.artistName}
        reportingPeriod={reportingPeriod}
      />

      {/* Target vs actual (the deal's own version of the pace Portfolio Overview grades it on) + contract terms */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch min-w-0">
        <div className="lg:col-span-2 min-w-0 flex flex-col">
          <TargetVsActualChart selectedDeal={selectedDeal} />
        </div>
        <div className="lg:col-span-1 min-w-0">
          <ContractTermsCard selectedDeal={selectedDeal} />
        </div>
      </div>

      <MonthlyBreakdownTable selectedDeal={selectedDeal} reportingPeriod={reportingPeriod} />
    </div>
  )
}

export default AdvanceDealDetailPage
